/**
 * SelectAdvisorPage Component (T049 - US3)
 * Page for selecting a financial advisor from the user's partner organization
 *
 * Features:
 * - Auth guard (redirects unauthenticated users to login) (FR-034)
 * - Eligibility check (only pending/skipped users can access)
 * - Partner logo display (FR-015)
 * - Advisor search with live dropdown
 * - Two-step confirmation flow (select → review with profile picture → confirm) (FR-021)
 * - Skip option (FR-026)
 * - Skeleton loading states
 * - Error handling (403, 404, 409)
 *
 * Styling based on docs/prototypes/SelectAdvisorPage.tsx
 * Green background (#026857) per FR-014
 *
 * FR-014 through FR-025, FR-026, FR-027, FR-028 through FR-035
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdvisorInfo } from '../types';
import { useAuth } from '@/features/authentication/hooks/useAuth';
import { useDocumentHead } from '@/shared/hooks/useDocumentHead';
import {
  useSelectionStatus,
  useSelectAdvisor,
  useSkipAdvisorSelection,
} from '../hooks/useAdvisorSelection';
import { AdvisorSearchDropdown } from '../components/AdvisorSearchDropdown';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';

/**
 * Get initials from first and last name
 */
const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export const SelectAdvisorPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [selectedAdvisor, setSelectedAdvisor] = useState<AdvisorInfo | null>(null);
  const [error, setError] = useState('');

  // Set document head
  useDocumentHead({
    title: 'Select Your Financial Advisor | bQuest',
    description: 'Choose your financial advisor from your partner organization to complete your bQuest account setup.',
    robots: 'noindex, nofollow',
  });

  // Fetch selection status to determine eligibility
  const { data: status, isLoading: isLoadingStatus } = useSelectionStatus();

  // Mutations
  const selectMutation = useSelectAdvisor();
  const skipMutation = useSkipAdvisorSelection();

  // Auth guard (FR-034)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/login');
    }
  }, [isAuthenticated, user, navigate]);

  // Eligibility check - redirect if not eligible
  useEffect(() => {
    if (!isLoadingStatus && status) {
      if (!status.requiresAdvisorSelection) {
        // User has already completed or is not eligible
        navigate('/dashboard');
      }
    }
  }, [status, isLoadingStatus, navigate]);

  const handleAdvisorSelect = (advisor: AdvisorInfo) => {
    setSelectedAdvisor(advisor);
    setError('');
  };

  const handleCancelSelection = () => {
    setSelectedAdvisor(null);
    setError('');
  };

  const handleConfirmSelection = async () => {
    if (!selectedAdvisor) return;

    setError('');

    try {
      await selectMutation.mutateAsync(selectedAdvisor.id);
      // Navigation is handled by the hook's onSuccess callback
    } catch (err) {
      console.error('Error selecting advisor:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to select advisor. Please try again.',
      );
      setSelectedAdvisor(null); // Clear selection on error
    }
  };

  const handleSkip = async () => {
    setError('');

    try {
      await skipMutation.mutateAsync();
      // Navigation is handled by the hook's onSuccess callback
    } catch (err) {
      console.error('Error skipping advisor selection:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to skip advisor selection. Please try again.',
      );
    }
  };

  // Loading state
  if (isLoadingStatus) {
    return (
      <div className="min-h-screen bg-[#026857] flex items-center justify-center">
        <div className="text-center" data-testid="loading-skeleton">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto"></div>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state (global error)
  if (error && !selectedAdvisor) {
    return (
      <div className="min-h-screen bg-[#026857] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-lg mb-4">{error}</p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-white hover:bg-opacity-90 text-[#026857]"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const partnerCompany = status?.partnerCompany;
  const isSubmitting = selectMutation.isPending || skipMutation.isPending;

  return (
    <div className="min-h-screen bg-[#026857] py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            {partnerCompany?.logo && (
              <img
                src={partnerCompany.logo}
                alt={`${partnerCompany.name} logo`}
                className="w-40 h-40 object-contain rounded-lg bg-white p-2 mx-auto mb-4 shadow-sm"
              />
            )}
            <h1 className="text-3xl font-recoleta text-white mb-2">
              Select Your Financial Advisor
            </h1>
            <p className="text-lg text-white mb-2">
              Choose your financial advisor from {partnerCompany?.name || 'your organization'} to complete your account setup.
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Search Section - Only show when no advisor is selected */}
          {!selectedAdvisor && (
            <>
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <AdvisorSearchDropdown onSelect={handleAdvisorSelect} />
              </div>

              {/* Skip Section */}
              <div className="flex items-center justify-center gap-4 my-8">
                <div className="h-[1px] bg-white opacity-20 flex-1 max-w-[200px]"></div>
                <p className="text-white text-xl font-recoleta opacity-80">Or</p>
                <div className="h-[1px] bg-white opacity-20 flex-1 max-w-[200px]"></div>
              </div>

              <div className="text-center mt-2 mb-16">
                <Button
                  onClick={handleSkip}
                  disabled={isSubmitting}
                  variant="outline"
                  className="bg-white hover:bg-opacity-90 text-[#026857] border-2 border-white px-12 py-4 text-xl font-recoleta font-bold rounded-xl transition-all transform hover:scale-105 shadow-lg"
                >
                  {skipMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#026857] mr-3"></div>
                      Skipping...
                    </>
                  ) : (
                    'Skip for now'
                  )}
                </Button>
              </div>
            </>
          )}

          {/* Selected Advisor Review Card - Two-step confirmation (FR-021) */}
          {selectedAdvisor && (
            <div className="bg-white border border-green-200 rounded-lg p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Avatar className="h-16 w-16">
                    {selectedAdvisor.logoOrHeadshot && (
                      <AvatarImage
                        src={selectedAdvisor.logoOrHeadshot}
                        alt={`${selectedAdvisor.firstName} ${selectedAdvisor.lastName}`}
                      />
                    )}
                    <AvatarFallback className="bg-[#026857] text-white text-base font-medium">
                      {getInitials(selectedAdvisor.firstName, selectedAdvisor.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-gray-600 text-xl">
                      {selectedAdvisor.firstName} {selectedAdvisor.lastName}
                    </p>
                    {selectedAdvisor.companyName && (
                      <p className="text-base text-gray-600">{selectedAdvisor.companyName}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCancelSelection}
                    disabled={isSubmitting}
                    className="px-4 py-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmSelection}
                    disabled={isSubmitting}
                    className="bg-[#026857] hover:bg-[#024d42] text-white px-6 py-2"
                  >
                    {selectMutation.isPending ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Confirming...
                      </>
                    ) : (
                      'Confirm Selection'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
};
